/**
 * CurrencyContext — Provides FX rates and currency conversion to the entire app.
 *
 * Reads the user's displayCurrency preference from Firestore and fetches
 * FX rates from the gateway. Caches rates in localStorage (1-hour TTL).
 *
 * Usage:
 *   const { convert, formatInUserCurrency, displayCurrency } = useCurrency();
 *   const audPrice = convert(usdPrice, 'USD');
 *   const label = formatInUserCurrency(usdPrice, 'USD'); // "A$1,234.56"
 */

import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { CacheService } from '../services/cacheService';
import { formatCurrency, convertCurrency } from '../utils/formatters';
import type { SupportedCurrency } from '../utils/formatters';
import { gatewayFetch } from '../services/gateway';
import { CurrencyContext, useOptionalCurrency } from './currencyContextCore';
import type { CurrencyContextValue } from './currencyContextCore';

export { useOptionalCurrency };
export type { CurrencyContextValue };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RATES_CACHE_KEY = 'fx_rates_usd';
const RATES_CACHE_TTL = 60 * 60 * 1000; // 1 hour
const CURRENCY_STORAGE_KEY = 'indexalpha-currency-preference';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const useCurrency = (): CurrencyContextValue => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CurrencyProviderProps {
  children: React.ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { userAccount } = useAuth();

  // Initialize from localStorage for instant display, sync with Firestore later
  const getStoredCurrency = (): SupportedCurrency => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(CURRENCY_STORAGE_KEY) as SupportedCurrency;
      if (stored) return stored;
    }
    return 'USD';
  };

  const [displayCurrency, setDisplayCurrencyState] = useState<SupportedCurrency>(getStoredCurrency);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const fetchedRef = useRef(false);

  // Sync with Firestore user preference
  useEffect(() => {
    const firestoreCurrency = userAccount?.preferences?.displayCurrency;
    if (firestoreCurrency) {
      setDisplayCurrencyState(firestoreCurrency);
      localStorage.setItem(CURRENCY_STORAGE_KEY, firestoreCurrency);
    }
  }, [userAccount?.preferences?.displayCurrency]);

  // Lazy FX rates fetcher — deferred to first call so the landing page
  // doesn't trigger a Cloud Function cold-start on every visit.
  // The 1-hour localStorage cache means the actual HTTP call is rare.
  const ensureRates = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const cached = CacheService.get<Record<string, number>>(RATES_CACHE_KEY, RATES_CACHE_TTL);
    if (cached) {
      setRates(cached);
      return;
    }

    setIsLoading(true);
    try {
      const resp = await gatewayFetch('fx', 'rates', { auth: 'none', params: { base: 'USD' } });
      if (resp.ok) {
        const data = await resp.json();
        if (data.rates) {
          setRates(data.rates);
          CacheService.set(RATES_CACHE_KEY, data.rates);
        }
      }
    } catch {
      // Graceful degradation: show values in original currency
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Set currency preference (persists to localStorage; Firestore save handled by UserProfile)
  const setDisplayCurrency = useCallback((currency: SupportedCurrency) => {
    setDisplayCurrencyState(currency);
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  }, []);

  // Convert amount from fromCurrency → user's displayCurrency
  const convert = useCallback(
    (amount: number, fromCurrency: string): number => {
      ensureRates(); // lazy-trigger fetch on first use
      return convertCurrency(amount, fromCurrency, displayCurrency, rates);
    },
    [ensureRates, displayCurrency, rates],
  );

  /* Convert and format with the display currency symbol.
   *
   * `frozenRates` lets a STORED report be read through the table it was
   * computed against instead of today's (item 17f). Without it a document
   * whose dateline says "as computed on the day" quietly restates its own
   * headline total every time the rates move. Omitted — which is the case for
   * every report stored before that shipped — it falls back to the live table,
   * exactly as before. */
  const formatInUserCurrency = useCallback(
    (
      amount: number | null | undefined,
      fromCurrency: string,
      frozenRates?: Record<string, number> | null,
    ): string => {
      // Still lazily fetched even when a frozen table is supplied: the reader
      // can switch display currency to one the frozen table happens to lack,
      // and the live table is the fallback for that.
      ensureRates();
      if (amount === null || amount === undefined || isNaN(amount)) {
        return formatCurrency(0, displayCurrency);
      }
      const table = frozenRates && Object.keys(frozenRates).length > 0 ? frozenRates : rates;
      const converted = convertCurrency(amount, fromCurrency, displayCurrency, table);
      return formatCurrency(converted, displayCurrency);
    },
    [ensureRates, displayCurrency, rates],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({
      displayCurrency,
      rates,
      convert,
      formatInUserCurrency,
      isLoading,
      setDisplayCurrency,
    }),
    [displayCurrency, rates, convert, formatInUserCurrency, isLoading, setDisplayCurrency],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};
